import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';

/**
 * IPCA mensal do Banco Central (SGS, série 433: variação % do mês). API pública, sem chave,
 * que aceita chamada direto do navegador.
 */
const SERIE_IPCA_URL = 'https://api.bcb.gov.br/dados/serie/bcdata.sgs.433/dados';

interface PontoSerie {
  /** "01/09/2026": o mês de referência. */
  data: string;
  /** Variação do mês em %, como texto ("0.82"). */
  valor: string;
}

/** IPCA acumulado num intervalo de meses. */
export interface IpcaAcumulado {
  /** Multiplicador do período (1,019 = +1,9%). */
  fator: number;
  /** Primeiro e último mês somados, como Date no dia 1. */
  inicio: Date;
  fim: Date;
  /** Nenhum mês desde a compra foi divulgado ainda: fator 1 e `inicio` é o mês da compra. */
  pendente?: boolean;
}

/** Acumula a série (produto de 1 + variação). Sem meses, não há o que corrigir. */
export function acumularIpca(serie: { data: string; valor: string }[]): IpcaAcumulado | null {
  const meses = serie
    .map(ponto => ({ mes: paraData(ponto.data), variacao: Number(ponto.valor) }))
    .filter(ponto => ponto.mes && Number.isFinite(ponto.variacao));
  if (!meses.length) return null;

  return {
    fator: meses.reduce((fator, ponto) => fator * (1 + ponto.variacao / 100), 1),
    inicio: meses[0].mes!,
    fim: meses[meses.length - 1].mes!
  };
}

/**
 * Primeiro dia do mês da data. A correção começa no mês da compra, como na Calculadora do
 * Cidadão do Banco Central (o mês inicial entra no índice).
 */
export function inicioDoMes(data: Date): Date {
  return new Date(data.getFullYear(), data.getMonth(), 1);
}

function paraData(ddmmaaaa: string): Date | null {
  const [dia, mes, ano] = ddmmaaaa.split('/').map(Number);
  if (!dia || !mes || !ano) return null;
  return new Date(ano, mes - 1, dia);
}

function formatarParametro(data: Date): string {
  const dd = String(data.getDate()).padStart(2, '0');
  const mm = String(data.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${data.getFullYear()}`;
}

@Injectable({ providedIn: 'root' })
export class IpcaService {
  /**
   * Cliente sem os interceptors do app: a chamada é para um site externo e não pode levar o
   * token de login nem passar pelo tratamento de erro da nossa API.
   */
  private readonly http = new HttpClient(inject(HttpBackend));
  /** Uma consulta por mês de início: vários itens comprados no mesmo mês usam a mesma. */
  private readonly cache = new Map<string, Observable<IpcaAcumulado | null>>();

  /**
   * IPCA acumulado do mês da compra até o último mês divulgado. Quando nenhum desses meses
   * saiu ainda, volta `pendente`. `null` só quando o Banco Central não respondeu.
   */
  acumuladoDesde(dataCompra: Date): Observable<IpcaAcumulado | null> {
    const inicio = inicioDoMes(dataCompra);

    const chave = `${inicio.getFullYear()}-${inicio.getMonth()}`;
    const cached = this.cache.get(chave);
    if (cached) return cached;

    const consulta = this.http
      .get<PontoSerie[]>(SERIE_IPCA_URL, {
        params: {
          formato: 'json',
          dataInicial: formatarParametro(inicio),
          dataFinal: formatarParametro(new Date())
        }
      })
      .pipe(
        map(
          serie =>
            acumularIpca(serie ?? []) ?? { fator: 1, inicio, fim: inicio, pendente: true }
        ),
        catchError((erro: unknown) => {
          // sem nenhum mês divulgado no intervalo, o SGS responde 404 ("Value(s) not found")
          if (erro instanceof HttpErrorResponse && erro.status === 404) {
            return of<IpcaAcumulado>({ fator: 1, inicio, fim: inicio, pendente: true });
          }
          // outra falha: tenta de novo no próximo tooltip em vez de guardar o erro
          this.cache.delete(chave);
          return of(null);
        }),
        shareReplay(1)
      );
    this.cache.set(chave, consulta);
    return consulta;
  }
}
